import { Box, ButtonBase, ClickAwayListener, Fade, Stack, Typography, type TypographyProps } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useTranslations } from 'next-intl'
import { useEffect, useRef } from 'react'

import type { SequenceFollowUp, SequenceState } from '~/hooks/useKeyboardShortcuts'

const Container = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(4),
  left: theme.spacing(4),
  zIndex: theme.zIndex.tooltip,
  padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
  backgroundColor: theme.palette.grey[900],
  color: theme.palette.common.white,
  borderRadius: theme.shape.borderRadius,
  fontFamily: 'monospace',
  fontSize: '0.875rem',
  boxShadow: theme.shadows[4],
  // On narrow viewports a long localized label (e.g. German "Schnellsuche-Filter")
  // could push the popup past the right edge of the screen. Cap the width to the
  // viewport minus the bottom-left + right insets so it always fits. On wider
  // screens the cap is loose enough to be a no-op — the popup sizes to content.
  maxWidth: `calc(100vw - ${theme.spacing(8)})`,
  // Tighten the bottom-left inset on mobile-class viewports so the popup doesn't
  // bump into mobile UI furniture (browser toolbars, on-screen keyboards). The
  // shortcut surface itself is keyboard-only (touch users can't press G/B), but
  // Bluetooth-keyboard tablet users still see this — and we want it not to
  // collide with viewport edges in landscape iPad.
  [theme.breakpoints.down('sm')]: {
    bottom: theme.spacing(2),
    left: theme.spacing(2),
    maxWidth: `calc(100vw - ${theme.spacing(4)})`
  }
}))

const KeyPill = styled('kbd')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 18,
  padding: `2px ${theme.spacing(0.75)}`,
  backgroundColor: 'rgba(255, 255, 255, 0.16)',
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.common.white,
  fontFamily: 'monospace',
  fontSize: '0.7rem',
  lineHeight: 1
}))

// Each follow-up renders as a `ButtonBase`-wrapped row (key pill + label).
// Hover lightens the background to give a clickable affordance without
// looking like an OS button — keeps the indicator's dense monospace look.
const FollowUpButton = styled(ButtonBase)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: `2px ${theme.spacing(0.5)}`,
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.common.white,
  cursor: 'pointer',
  transition: 'background-color 0.12s ease',
  // `minWidth: 0` lets the flex child shrink past its content's intrinsic width.
  // Without it the row refuses to honor the Container's `maxWidth` cap on
  // narrow viewports — the label's `white-space` rule wins and pushes the row
  // past the container edge. Pairs with the mobile `white-space: normal`
  // override on `FollowUpLabel` below — per Copilot PR #2266 review.
  minWidth: 0,
  '&:hover, &:focus-visible': {
    backgroundColor: 'rgba(255, 255, 255, 0.08)'
  },
  '&:focus-visible': {
    outline: `2px solid rgba(255, 255, 255, 0.5)`,
    outlineOffset: 1
  }
}))

// Generic `<TypographyProps>` on the styled wrapper preserves the `component`
// prop in the resulting component's prop type — without it, the styled
// returned component drops to a default-element-only signature and `component`
// fails tsc (caught during pre-push gate on the first build attempt).
const FollowUpLabel = styled(Typography)<TypographyProps>(({ theme }) => ({
  fontSize: '0.75rem',
  lineHeight: 1,
  color: theme.palette.common.white,
  marginLeft: theme.spacing(0.25),
  // On desktop, keep labels on one line — the Container's `max-width` cap
  // gives them all the horizontal room they need. On narrow viewports, allow
  // wrap so a long localized label (German "Schnellsuche-Filter") doesn't
  // push past the cap and out of the viewport. Pairs with the `minWidth: 0`
  // on `FollowUpButton` above so the flex row actually respects the
  // container width — per Copilot PR #2266 review.
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.2
  }
}))

type Props = {
  sequence: SequenceState | null
  /** Called by `ClickAwayListener` when the user clicks anywhere outside the
   *  indicator. Owned by `useKeyboardShortcuts` so the click-away dismissal
   *  goes through the same code path as keyboard / `Esc` dismissal. */
  dismissSequence: () => void
}

export default function SequenceIndicator({ sequence, dismissSequence }: Props) {
  const t = useTranslations('components.KeyboardShortcutsOverlay')

  // Retain the last non-null state so the Fade exit animation shows the content
  // fading out instead of an empty pill.
  const lastRef = useRef<SequenceState | null>(sequence)
  useEffect(() => {
    if (sequence !== null) lastRef.current = sequence
  }, [sequence])
  const display = sequence ?? lastRef.current

  // `ClickAwayListener` requires a single React element child; only attach the
  // dismiss handler while the sequence is actually showing — otherwise stray
  // clicks elsewhere in the app would still fire `dismissSequence` when no
  // sequence is pending (harmless but wasteful re-renders).
  const handleClickAway = sequence === null ? () => {} : dismissSequence

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Fade in={sequence !== null} unmountOnExit>
        <Container
          aria-live="polite"
          role="region"
          aria-label={t('sequenceIndicator_ariaLabel')}
          // Marker for the document-level keydown handler in `useKeyboardShortcuts`.
          // This component renders focusable buttons but has NO key handlers of
          // its own — Tab traversal and Space/Enter activation are the browser's
          // default behavior on focused `<button>` elements. The hook's document-
          // level handler uses `event.target.closest('[data-sequence-indicator="true"]')`
          // to detect keystrokes originating from within the popup and skip its
          // own "dismiss on non-relevant key" branch for those events. Without
          // this marker, a focused row's Space keyup would land on a button that's
          // already been unmounted by the dismissal effect. Consumer-side check
          // lives in `useKeyboardShortcuts.ts` (per milotoor PR #2259 review).
          data-sequence-indicator="true"
        >
          {display && (
            // Vertical stack — R3.3 replaced the horizontal comma-separated
            // layout because 9 G-leader entries (D V S C R A M B ,) plus their
            // labels were cramped on one line. The vertical list also scans
            // better, especially with longer German / French labels. `max-height`
            // + `overflowY: auto` is a future-proof guard in case the G or B
            // followup lists ever grow beyond what the viewport can show.
            <Stack direction="column" spacing={0.75}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <KeyPill>{display.prefix}</KeyPill>
                <Typography component="span" variant="inherit">
                  →
                </Typography>
              </Stack>
              <Stack
                direction="column"
                alignItems="flex-start"
                spacing={0.25}
                sx={{ maxHeight: '60vh', overflowY: 'auto' }}
              >
                {display.followUps.map((followUp) => (
                  <FollowUpRow key={followUp.key} followUp={followUp} />
                ))}
              </Stack>
            </Stack>
          )}
        </Container>
      </Fade>
    </ClickAwayListener>
  )
}

/** A single clickable follow-up row inside the indicator. Encapsulated as its
 *  own component so the button event handlers and accessibility props don't
 *  clutter the main render path. */
function FollowUpRow({ followUp }: { followUp: SequenceFollowUp }) {
  const t = useTranslations('components.KeyboardShortcutsOverlay')
  return (
    <FollowUpButton
      type="button"
      onClick={followUp.onClick}
      aria-label={t('sequenceIndicator_followUpAriaLabel', { label: followUp.label, key: followUp.key })}
    >
      <KeyPill>{followUp.key}</KeyPill>
      <FollowUpLabel component="span" variant="inherit">
        {followUp.label}
      </FollowUpLabel>
    </FollowUpButton>
  )
}
